import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Shield, Trash2, CheckCircle2, XCircle, UserX, RefreshCw, ExternalLink, Flag, AlertTriangle } from "lucide-react";

const STATUS_TABS = [
  { key: "open", label: "Open" },
  { key: "resolved", label: "Resolved" },
  { key: "dismissed", label: "Dismissed" },
];

const TARGET_LINK = {
  listing: (id) => `/marketplace/${id}`,
  event: (id) => `/events/${id}`,
  job: (id) => `/jobs/${id}`,
  need: (id) => `/needs/${id}`,
  business: (slug) => `/business/${slug}`,
  user: () => null,
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatCard({ label, value, tone = "pine" }) {
  const tones = {
    pine: "text-[#1E3A2F] bg-white border-[#E2DED0]",
    red: "text-[#C05A3D] bg-[#C05A3D]/5 border-[#C05A3D]/30",
    gold: "text-[#7a5a1a] bg-[#DDA74A]/10 border-[#DDA74A]/40",
  };
  return (
    <div className={`rounded-2xl border p-5 ${tones[tone]}`} data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-3xl font-display">{value}</div>
      <div className="text-xs uppercase tracking-[0.18em] mt-1 opacity-80">{label}</div>
    </div>
  );
}

function ReportRow({ report, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const target = report.target;
  const href = target ? TARGET_LINK[report.target_type]?.(target.id) : null;

  const call = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const resolveReport = () => call(() => api.patch(`/admin/reports/${report.id}`, { status: "resolved", action_taken: "reviewed" }), "Marked resolved");
  const dismissReport = () => call(() => api.patch(`/admin/reports/${report.id}`, { status: "dismissed", action_taken: "no_violation" }), "Report dismissed");
  const removeContent = () => {
    if (!window.confirm("Remove this content from the site? This can be undone by an admin later via the database.")) return;
    return call(() => api.delete(`/admin/content/${report.target_type}/${report.target_id}`), "Content removed");
  };
  const banAuthor = () => {
    if (!window.confirm("Suspend this member? They won't be able to sign in or post.")) return;
    return call(() => api.patch(`/admin/users/${report.target_id}/ban`, { banned: true, reason: report.reason }), "Member suspended");
  };
  const unbanAuthor = () =>
    call(() => api.patch(`/admin/users/${report.target_id}/ban`, { banned: false, reason: "" }), "Member reinstated");

  const isUserReport = report.target_type === "user";
  const targetMissing = !target;
  const targetBanned = target?.status === "banned";
  const targetRemoved = target?.status === "removed";

  return (
    <div className="abm-card p-5 space-y-4" data-testid={`admin-report-${report.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] font-semibold text-[#C05A3D]"><Flag className="w-3 h-3" /> {report.reason}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#F7F4EB] text-[#5C5C5C] capitalize">{report.target_type}</span>
            {targetRemoved && <span className="text-xs px-2 py-0.5 rounded-full bg-black/80 text-white">Removed</span>}
            {targetBanned && <span className="text-xs px-2 py-0.5 rounded-full bg-red-700 text-white">Banned</span>}
            {targetMissing && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-200 text-yellow-900 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Missing</span>}
          </div>
          <div className="mt-2 font-display text-lg text-[#1E3A2F] truncate">
            {target?.title || <span className="text-[#5C5C5C] italic">Deleted content</span>}
          </div>
          {target?.subtitle && <div className="text-xs text-[#5C5C5C] mt-0.5">{target.subtitle}</div>}
          {report.details && <p className="mt-3 text-sm text-[#3A3A3A] whitespace-pre-line">"{report.details}"</p>}
        </div>
        <div className="text-right text-xs text-[#5C5C5C] shrink-0">
          <div>Reported {fmtDate(report.created_at)}</div>
          <div className="truncate max-w-[220px]">
            By <span className="text-[#1E3A2F]">{report.reporter_email || (report.reporter_id ? "member" : "anonymous")}</span>
          </div>
          {report.resolved_at && <div className="mt-1">Actioned {fmtDate(report.resolved_at)}</div>}
          {report.action_taken && <div className="mt-1 italic">{report.action_taken.replace(/_/g, " ")}</div>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#E2DED0]">
        {href && (
          <Button variant="ghost" size="sm" onClick={() => nav(href)} data-testid={`report-open-${report.id}`}>
            <ExternalLink className="w-4 h-4 mr-1" /> Open
          </Button>
        )}

        {report.status === "open" && !isUserReport && !targetRemoved && target && (
          <Button variant="ghost" size="sm" disabled={busy} onClick={removeContent} className="text-red-600 hover:bg-red-50" data-testid={`report-remove-${report.id}`}>
            <Trash2 className="w-4 h-4 mr-1" /> Remove content
          </Button>
        )}

        {report.status === "open" && isUserReport && !targetBanned && target && (
          <Button variant="ghost" size="sm" disabled={busy} onClick={banAuthor} className="text-red-600 hover:bg-red-50" data-testid={`report-ban-${report.id}`}>
            <UserX className="w-4 h-4 mr-1" /> Suspend member
          </Button>
        )}

        {report.status === "open" && isUserReport && targetBanned && (
          <Button variant="ghost" size="sm" disabled={busy} onClick={unbanAuthor} data-testid={`report-unban-${report.id}`}>
            <RefreshCw className="w-4 h-4 mr-1" /> Reinstate
          </Button>
        )}

        {report.status === "open" && (
          <>
            <Button variant="ghost" size="sm" disabled={busy} onClick={resolveReport} className="text-[#1E3A2F]" data-testid={`report-resolve-${report.id}`}>
              <CheckCircle2 className="w-4 h-4 mr-1" /> Mark resolved
            </Button>
            <Button variant="ghost" size="sm" disabled={busy} onClick={dismissReport} data-testid={`report-dismiss-${report.id}`}>
              <XCircle className="w-4 h-4 mr-1" /> Dismiss
            </Button>
          </>
        )}

        {report.status !== "open" && (
          <span className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C]">{report.status}</span>
        )}
      </div>
    </div>
  );
}

export default function AdminModeration() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("open");
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.role === "admin";

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const [{ data: r }, { data: s }] = await Promise.all([
        api.get(`/admin/reports${status ? `?status=${status}` : ""}`),
        api.get("/admin/stats"),
      ]);
      setReports(r.items || []);
      setStats(s);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load moderation data");
    } finally {
      setBusy(false);
    }
  }, [status]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      nav("/login");
      return;
    }
    if (!isAdmin) return;
    refresh();
  }, [loading, user, isAdmin, refresh, nav]);

  const grouped = useMemo(() => reports, [reports]);

  if (loading) {
    return <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 text-[#5C5C5C]">Loading…</div>;
  }

  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 text-center">
        <Shield className="w-8 h-8 text-[#C05A3D] mx-auto" />
        <h1 className="font-display text-3xl text-[#1E3A2F] mt-4">Admins only</h1>
        <p className="text-[#5C5C5C] mt-2">You need moderator access to view the reports dashboard.</p>
        <Link to="/" className="inline-block mt-6 rounded-full abm-btn-primary px-5 h-10 leading-10 text-white text-sm">Back home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 space-y-8" data-testid="admin-moderation-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 abm-chip"><Shield className="w-3 h-3" /> Moderation</div>
          <h1 className="mt-3 font-display text-4xl text-[#1E3A2F]">Community reports</h1>
          <p className="text-[#5C5C5C] mt-1">Review flagged content, remove violations, and protect the community.</p>
        </div>
        <Button onClick={refresh} disabled={busy} variant="ghost" data-testid="admin-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </header>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Open reports" value={stats.open_reports} tone="red" />
          <StatCard label="Resolved" value={stats.resolved_reports} />
          <StatCard label="Dismissed" value={stats.dismissed_reports} />
          <StatCard label="Banned members" value={stats.banned_users} tone="gold" />
          <StatCard label="Removed content" value={stats.removed_content} tone="gold" />
        </div>
      )}

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="bg-white border border-[#E2DED0]">
          {STATUS_TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`admin-tab-${t.key}`} className="data-[state=active]:bg-[#1E3A2F] data-[state=active]:text-white">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {STATUS_TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-6">
            {busy && !grouped.length ? (
              <div className="text-center py-14 text-[#5C5C5C]">Loading reports…</div>
            ) : grouped.length ? (
              <div className="grid gap-4">
                {grouped.map((r) => <ReportRow key={r.id} report={r} onRefresh={refresh} />)}
              </div>
            ) : (
              <div className="text-center py-16 border border-dashed border-[#E2DED0] rounded-2xl bg-white/60" data-testid="admin-empty-state">
                <Shield className="w-8 h-8 text-[#C05A3D] mx-auto" />
                <div className="mt-3 font-display text-xl text-[#1E3A2F]">Nothing here — the community is calm</div>
                <p className="text-sm text-[#5C5C5C] mt-1">No {t.label.toLowerCase()} reports right now.</p>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
